import { useCallback, useState, type ReactNode } from 'react';

/**
 * Form primitives.
 *
 * Deliberately small: labelled fields, an error slot and a save/cancel footer. The legacy
 * forms were plain Bootstrap inputs with server-rendered validation messages, and the API
 * still returns one message per rejection, so there is nothing here that tries to be a
 * validation framework. Whatever the server says goes into the error slot and a toast.
 */

type FieldProps = {
  label: string;
  htmlFor?: string;
  hint?: string;
  required?: boolean;
  /** Server (or pre-flight) message for this field. Replaces the hint while present. */
  error?: string;
  children: ReactNode;
};

export function Field({ label, htmlFor, hint, required, error, children }: FieldProps) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>
        {label}
        {required && (
          <span style={{ color: 'var(--danger)', marginLeft: 2 }} aria-hidden="true">*</span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {/* The error replaces the hint rather than stacking under it: the hint restates the
          rule the error has just reported broken, and two lines saying the same thing is
          harder to read than one. */}
      {error
        ? <span className="field__error" id={htmlFor ? `${htmlFor}-error` : undefined} role="alert">{error}</span>
        : hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

/** Wires an input to its message for assistive tech, and turns on the invalid styling. */
const invalidProps = (id: string, error?: string) =>
  (error ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {});

type TextProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
  error?: string;
  type?: 'text' | 'email' | 'password' | 'date' | 'month' | 'datetime-local';
  placeholder?: string;
  maxLength?: number;
};

export function TextField({
  id, label, value, onChange, required, hint, error, type = 'text', placeholder, maxLength,
}: TextProps) {
  return (
    <Field label={label} htmlFor={id} hint={hint} required={required} error={error}>
      <input
        id={id}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        {...invalidProps(id, error)}
      />
    </Field>
  );
}

type NumberProps = {
  id: string;
  label: string;
  value: number | string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
  error?: string;
};

export function NumberField({ id, label, value, onChange, required, hint, error }: NumberProps) {
  return (
    <Field label={label} htmlFor={id} hint={hint} required={required} error={error}>
      <input
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        className="tabular"
        {...invalidProps(id, error)}
      />
    </Field>
  );
}

type Option = { value: string | number; label: string };

type SelectProps = {
  id: string;
  label: string;
  value: string | number;
  options: Option[];
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
  error?: string;
  placeholder?: string;
};

export function SelectField({
  id, label, value, options, onChange, required, hint, error, placeholder = '— select —',
}: SelectProps) {
  return (
    <Field label={label} htmlFor={id} hint={hint} required={required} error={error}>
      <select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...invalidProps(id, error)}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function CheckboxField({
  id, label, checked, onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="check" style={{ marginBottom: 'var(--sp-3)' }}>
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 'auto' }}
      />
      <span>{label}</span>
    </label>
  );
}

/** Two-column grid that collapses on narrow screens. */
export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="field-row">{children}</div>;
}

export function FormActions({
  busy, onSave, onCancel, saveLabel = 'Save',
}: {
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
}) {
  return (
    <div
      className="row"
      style={{
        gap: 'var(--sp-2)',
        marginTop: 'var(--sp-5)',
        paddingTop: 'var(--sp-4)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <button type="button" className="btn btn--primary" onClick={onSave} disabled={busy}>
        {busy ? <><span className="btn__spin" />Saving…</> : saveLabel}
      </button>
      <button type="button" className="btn" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}

/**
 * Minimal form state. Holds a draft object, a typed setter, and whatever the server said was
 * wrong with the last attempt.
 *
 * The rules themselves still live on the server - duplicating them in the browser is how the
 * two drift apart. What is held here is only the *reported* failures, so each one can be
 * shown against the field it belongs to instead of as one anonymous toast.
 */
export function useForm<T extends object>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Editing a field retracts its complaint. Leaving the message up while the user fixes the
  // value makes a corrected field look broken. Exposed as clearError for the few fields a
  // form keeps outside `values` (a separate useState) - they cannot go through `set`.
  const clearError = useCallback((key: string) => {
    setErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    clearError(key as string);
  }, [clearError]);

  const setText = useCallback(<K extends keyof T>(key: K) =>
    (value: string) => set(key, value as T[K]), [set]);

  const setNumber = useCallback(<K extends keyof T>(key: K) =>
    (value: string) => set(key, (value === '' ? '' : Number(value)) as T[K]), [set]);

  const setBool = useCallback(<K extends keyof T>(key: K) =>
    (value: boolean) => set(key, value as T[K]), [set]);

  return { values, setValues, set, setText, setNumber, setBool, errors, setErrors, clearError };
}
